import CryptoJS from 'crypto-js'

// 服务端预置公钥
const PRESET_SERVER_PUBLIC_KEY_BYTES =
  '04c5c00c4f8d1197cc7c3167c52bf7acb054d722f0ef08dcd7e0883236e0d72a3868d9750cb47fa4619248f3d83f0f662671dadc6e2d31c2f41db0161651c7c076'

// 生成客户端私钥和密钥
export const getKeyPair = () => {
  const rawPublicKey = getClientKey()?.public_key || ''
  const rawPrivateKey = getClientKey()?.private_key || ''
  let promise: Promise<any>
  if (rawPublicKey && rawPrivateKey) {
    promise = new Promise(async (resolve) => {
      const [publicKey, privateKey] = await Promise.all([
        window.crypto.subtle.importKey(
          'raw',
          new Uint8Array(rawPublicKey.match(/[\da-f]{2}/gi)!.map((h) => parseInt(h, 16))).buffer,
          {
            name: 'ECDH',
            namedCurve: 'P-256',
          },
          true,
          []
        ),
        window.crypto.subtle.importKey(
          'pkcs8',
          new Uint8Array(rawPrivateKey.match(/[\da-f]{2}/gi)!.map((h) => parseInt(h, 16))).buffer,
          {
            name: 'ECDH',
            namedCurve: 'P-256',
          },
          true,
          ['deriveKey', 'deriveBits']
        ),
      ])
      resolve({ publicKey, privateKey })
    })
  } else {
    promise = window.crypto.subtle.generateKey(
      {
        name: 'ECDH',
        namedCurve: 'P-256',
      },
      true,
      ['deriveKey', 'deriveBits']
    )
  }
  return async () => {
    const keyPair = await promise
    const [publicKey, privateKey] = await Promise.all([
      window.crypto.subtle.exportKey('raw', keyPair.publicKey),
      window.crypto.subtle.exportKey('jwk', keyPair.privateKey),
    ])
    window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey).then((k) => {
      setClientKey(
        new Uint8Array(publicKey).reduce((i, v) => i + `00${v.toString(16)}`.substr(-2), ''),
        new Uint8Array(k).reduce((i, v) => {
          return i + `00${v.toString(16)}`.substr(-2)
        }, '')
      )
    })
    return { publicKey, privateKey, keyPair }
  }
}

// 存储本地公私钥
function setClientKey(public_key: string, private_key: string) {
  const obj = [window.btoa(public_key), window.btoa(private_key)]
  window.sessionStorage.setItem('clientKey', JSON.stringify(obj))
}

// 取出公私匙
function getClientKey() {
  const val = JSON.parse(window.sessionStorage.getItem('clientKey') || '[]')
  let obj = undefined
  if (val) {
    console.log('val--->', val);

    obj = {
      public_key: window.atob(val[0]),
      private_key: window.atob(val[1]),
    }
  }
  return obj
}

// v2生成密钥
function deriveSecretKey(privateKey: CryptoKey, publicKey: CryptoKey) {
  return window.crypto.subtle.deriveKey(
    {
      name: 'ECDH',
      public: publicKey,
    },
    privateKey,
    {
      name: 'AES-CBC',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  )
}
/** 增加服务器返回的非预设公钥存取 */
function getServerPublicKeyBytes() {
  // 这里从sessionStorage里面取出来
  return window.sessionStorage.getItem('public_key')
  // return getUserInfo()?.server_secret_info?.public_key || null
}

async function getIVFromKey(key: CryptoKey) {
  const publicKey = await window.crypto.subtle.exportKey('raw', key)
  return new Uint8Array(publicKey.slice(0, 16))
}

async function generateSecretKey() {
  // 服务端公钥字节数据
  const serverKeyBytes = getServerPublicKeyBytes() || PRESET_SERVER_PUBLIC_KEY_BYTES
  // 把服务端公钥转 ArrayBuffer
  const typedArray = new Uint8Array(serverKeyBytes!.match(/[\da-f]{2}/gi)!.map((h) => parseInt(h, 16)))
  const publicKeyBuffer = typedArray.buffer

  const { keyPair } = await getKeyPair()()
  const publicKey = await importKey(publicKeyBuffer)
  // 通过客户端私钥和服务端公钥生成密钥
  return deriveSecretKey(keyPair.privateKey, publicKey)
}

// v2处理服务端公钥导入生成CryptoKey
async function importKey(rawKey: BufferSource) {
  const result = await window.crypto.subtle.importKey(
    'raw',
    rawKey,
    {
      name: 'ECDH',
      namedCurve: 'P-256',
    },
    true,
    []
  )
  return result
}

// v2把加密后的arrayBuffer转Base64
function arrayBufferToBase64(buffer: ArrayBufferLike) {
  var binary = ''
  var bytes = new Uint8Array(buffer)
  var len = bytes.byteLength
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function base64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * AES-CBC加密v2
 * AESEncrypt('需要加密的字符串')
 */

export async function AESEncrypt(text: string) {
  const secretKey = await generateSecretKey()
  const encoder = new TextEncoder()
  const encoded = encoder.encode(text)
  // 赋值iv
  const iv = await getIVFromKey(secretKey)
  const ciphertext = await window.crypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv,
    },
    secretKey,
    encoded
  )
  return arrayBufferToBase64(ciphertext)
}

/**
 * AES-CBC解密v2
 * 使用列子：await AESDecrypt('需要解密的AES CBC加密后的base64字符串')
 */
export async function AESDecrypt(text: string) {
  const secretKey = await generateSecretKey()
  const iv = await getIVFromKey(secretKey)
  const decoder = new TextDecoder()
  const cipherText = await window.crypto.subtle.decrypt(
    {
      name: 'AES-CBC',
      iv,
    },
    secretKey,
    base64ToUint8Array(text)
  )
  return decoder.decode(cipherText)
}

/**
 * AES-CBC解密v3
 * 使用列子：await AesCBCDecryptV3('需要解密的AES CBC加密后的base64字符串','协商key')
 */
export const AesCBCDecryptV3 = async (sourceData: string, shareKey: string) => {
  const blockSize = 16
  const {
    AES,
    lib,
    pad,
    enc: { Hex, Base64, Utf8 },
  } = CryptoJS

  const keyByte = Hex.parse(shareKey)

  if (keyByte.sigBytes < blockSize) {
    throw new Error('AesCBCDecrypt:keyByte too short')
  }

  const byteValue = Base64.parse(sourceData)

  if (byteValue.sigBytes < blockSize) {
    throw new Error('sourceData too short')
  }

  const iv = byteValue.words.slice(0, blockSize / 4)
  const encryptedBytes = byteValue.words.slice(blockSize / 4)

  const cipher = lib.CipherParams.create({
    ciphertext: lib.WordArray.create(encryptedBytes),
  })

  const decryptedBytes = AES.decrypt(cipher, keyByte, {
    iv: lib.WordArray.create(iv),
    padding: pad.Pkcs7,
  })

  return decryptedBytes.toString(Utf8)
}
